import React, { useCallback } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import ListItem from '@tiptap/extension-list-item';
import BulletList from '@tiptap/extension-bullet-list';
import OrderedList from '@tiptap/extension-ordered-list';
import TextAlign from '@tiptap/extension-text-align';
import Link from '@tiptap/extension-link';
import classes from './RichTextEditor.module.scss';
import i18n from '../../utils/i18n';

// Toolbar button component
const MenuButton = ({ onClick, isActive, children, title }) => (
	<button
		onClick={onClick}
		className={`${classes.menuButton} ${isActive ? classes.isActive : ''}`}
		title={title}
		type="button">
		{children}
	</button>
);

const RichTextEditor = ({
	value = '',
	onChange,
	placeholder = i18n.t('provider.introduction'),
	height = '200px',
	showToolbar = true,
}) => {
	const editor = useEditor({
		extensions: [
			StarterKit.configure({
				bulletList: {
					keepMarks: true,
					keepAttributes: false,
				},
				orderedList: {
					keepMarks: true,
					keepAttributes: false,
				},
			}),
			ListItem,
			BulletList,
			OrderedList,
			TextAlign.configure({
				types: ['heading', 'paragraph'],
			}),
			Link.configure({
				openOnClick: false,
				HTMLAttributes: {
					class: 'link',
				},
			}),
		],
		content: value,
		onUpdate: ({ editor }) => {
			const html = editor.getHTML();
			onChange(html);
		},
		editorProps: {
			attributes: {
				class: classes.editorContent,
				style: `min-height: ${height}; font-family: 'Satoshi', sans-serif; font-size: 14px;`,
			},
		},
	});

	// Toolbar functions
	const setBold = useCallback(() => {
		editor?.chain().focus().toggleBold().run();
	}, [editor]);

	const setItalic = useCallback(() => {
		editor?.chain().focus().toggleItalic().run();
	}, [editor]);

	const setStrike = useCallback(() => {
		editor?.chain().focus().toggleStrike().run();
	}, [editor]);

	const setCode = useCallback(() => {
		editor?.chain().focus().toggleCode().run();
	}, [editor]);

	const setHeading = useCallback(
		(level) => {
			editor?.chain().focus().toggleHeading({ level }).run();
		},
		[editor]
	);

	const setBulletList = useCallback(() => {
		editor?.chain().focus().toggleBulletList().run();
	}, [editor]);

	const setOrderedList = useCallback(() => {
		editor?.chain().focus().toggleOrderedList().run();
	}, [editor]);

	const setTextAlign = useCallback(
		(alignment) => {
			editor?.chain().focus().setTextAlign(alignment).run();
		},
		[editor]
	);

	const setLink = useCallback(() => {
		const previousUrl = editor.getAttributes('link').href;
		const url = window.prompt('URL', previousUrl);

		// cancelled
		if (url === null) {
			return;
		}

		// empty
		if (url === '') {
			editor.chain().focus().extendMarkRange('link').unsetLink().run();
			return;
		}

		// Ensure the URL is clean and absolute
		let cleanUrl = url.trim();

		// If it doesn't start with http:// or https://, add https://
		if (!cleanUrl.match(/^https?:\/\//)) {
			cleanUrl = `https://${cleanUrl}`;
		}

		// update link
		editor
			.chain()
			.focus()
			.extendMarkRange('link')
			.setLink({ href: cleanUrl })
			.run();
	}, [editor]);

	const unsetLink = useCallback(() => {
		editor?.chain().focus().unsetLink().run();
	}, [editor]);

	if (!editor) {
		return null;
	}

	return (
		<div className={classes.richTextEditor}>
			{showToolbar && (
				<div className={classes.toolbar}>
					{/* Text Formatting */}
					<MenuButton
						onClick={setBold}
						isActive={editor.isActive('bold')}
						title={i18n.t('general.bold')}>
						<strong>B</strong>
					</MenuButton>
					<MenuButton
						onClick={setItalic}
						isActive={editor.isActive('italic')}
						title={i18n.t('general.italic')}>
						<em>I</em>
					</MenuButton>
					<MenuButton
						onClick={setStrike}
						isActive={editor.isActive('strike')}
						title={i18n.t('general.strikethrough')}>
						<s>S</s>
					</MenuButton>
					<MenuButton
						onClick={setCode}
						isActive={editor.isActive('code')}
						title={i18n.t('general.code')}>
						&lt;/&gt;
					</MenuButton>

					<div className={classes.separator} />

					{/* Headings */}
					<MenuButton
						onClick={() => setHeading(1)}
						isActive={editor.isActive('heading', { level: 1 })}
						title={i18n.t('general.heading1')}>
						H1
					</MenuButton>
					<MenuButton
						onClick={() => setHeading(2)}
						isActive={editor.isActive('heading', { level: 2 })}
						title={i18n.t('general.heading2')}>
						H2
					</MenuButton>
					<MenuButton
						onClick={() => setHeading(3)}
						isActive={editor.isActive('heading', { level: 3 })}
						title={i18n.t('general.heading3')}>
						H3
					</MenuButton>

					<div className={classes.separator} />

					{/* Lists */}
					<MenuButton
						onClick={setBulletList}
						isActive={editor.isActive('bulletList')}
						title={i18n.t('general.bulletlist')}>
						•
					</MenuButton>
					<MenuButton
						onClick={setOrderedList}
						isActive={editor.isActive('orderedList')}
						title={i18n.t('general.numberedlist')}>
						1.
					</MenuButton>

					<div className={classes.separator} />

					{/* Text Alignment */}
					<MenuButton
						onClick={() => setTextAlign('left')}
						isActive={editor.isActive({ textAlign: 'left' })}
						title={i18n.t('general.alignleft')}>
						⬅
					</MenuButton>
					<MenuButton
						onClick={() => setTextAlign('center')}
						isActive={editor.isActive({ textAlign: 'center' })}
						title={i18n.t('general.aligncenter')}>
						⬆
					</MenuButton>
					<MenuButton
						onClick={() => setTextAlign('right')}
						isActive={editor.isActive({ textAlign: 'right' })}
						title={i18n.t('general.alignright')}>
						➡
					</MenuButton>
					<MenuButton
						onClick={() => setTextAlign('justify')}
						isActive={editor.isActive({ textAlign: 'justify' })}
						title={i18n.t('general.alignjustify')}>
						⬌
					</MenuButton>

					<div className={classes.separator} />

					{/* Link */}
					<MenuButton
						onClick={setLink}
						isActive={editor.isActive('link')}
						title={i18n.t('general.link')}>
						🔗
					</MenuButton>
					{editor.isActive('link') && (
						<MenuButton
							onClick={unsetLink}
							title={i18n.t('general.removelink')}>
							❌
						</MenuButton>
					)}
				</div>
			)}

			<div className={classes.editorWrapper}>
				<EditorContent editor={editor} placeholder={placeholder} />
			</div>
		</div>
	);
};

export default RichTextEditor;
